import React, { useState, useEffect } from "react";
import { Loader2, Save, RefreshCw } from "lucide-react";
import ImageGalleryEditor from "./ImageGalleryEditor";
import ColorPicker from "./ColorPicker";
import FontSelector from "./FontSelector";
import DynamicDataEditor from "./DynamicDataEditor";
import { apiService } from "../../service/Api";

/**
 * Brand Assets Manager - Parent Component
 * Orchestrates all brand asset editors (images, colors, fonts, data)
 * Can be used in onboarding or dashboard
 */
export default function BrandAssetsManager({ 
  onSave, 
  showSaveButton = true,
  autoSave = false,
  className = "" 
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const [assets, setAssets] = useState({
    images: [],
    fonts: [],
    colours: {},
    data: {}
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (autoSave && hasChanges) {
      const timer = setTimeout(() => {
        handleSave();
      }, 2000); // Auto-save after 2 seconds of no changes
      return () => clearTimeout(timer);
    }
  }, [assets, autoSave, hasChanges]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTailspinAssets();
      
      if (response.data.success) {
        setAssets({
          images: response.data.images || [],
          fonts: response.data.fonts || [],
          colours: response.data.colours || {},
          data: response.data.data || {}
        });
      } else {
        setError(response.data.message || "Failed to load assets");
      }
    } catch (err) {
      console.error("Error fetching assets:", err);
      setError("Failed to load brand assets");
    } finally {
      setLoading(false);
    }
  };

  const handleImagesChange = (newImages) => {
    setAssets(prev => ({ ...prev, images: newImages }));
    setHasChanges(true);
  };

  const handleFontsChange = (newFonts) => {
    setAssets(prev => ({ ...prev, fonts: newFonts }));
    setHasChanges(true);
  };

  const handleColoursChange = (newColours) => {
    setAssets(prev => ({ ...prev, colours: newColours }));
    setHasChanges(true);
  };

  const handleDataChange = (newData) => {
    setAssets(prev => ({ ...prev, data: newData }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Prepare the payload for bulk upsert
      const updates = [
        { key: "images", value: assets.images, value_type: "json" },
        { key: "fonts", value: assets.fonts, value_type: "json" },
        { key: "colours", value: assets.colours, value_type: "json" },
        { key: "data", value: assets.data, value_type: "json" }
      ];

      const response = await apiService.updateTailspinAssets(updates);

      if (response.data.success) {
        setSuccessMessage("Brand assets saved successfully!");
        setHasChanges(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
        
        if (onSave) {
          onSave(assets);
        }
      } else {
        setError(response.data.message || "Failed to save assets");
      }
    } catch (err) {
      console.error("Error saving assets:", err);
      setError("Failed to save brand assets");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading brand assets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Header with Save Button */}
      {showSaveButton && (
        <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Brand Assets</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage your brand images, colors, fonts, and data
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAssets}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {hasChanges && !autoSave && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
          You have unsaved changes. Click "Save Changes" to persist them.
        </div>
      )}

      {/* Images Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <ImageGalleryEditor
          images={assets.images}
          onChange={handleImagesChange}
          title="Brand Images"
        />
      </div>

      {/* Colors Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <ColorPicker
          colors={assets.colours}
          onChange={handleColoursChange}
          title="Brand Colors"
        />
      </div>

      {/* Fonts Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <FontSelector
          fonts={assets.fonts}
          onChange={handleFontsChange}
          title="Brand Fonts"
        />
      </div>

      {/* Dynamic Data Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <DynamicDataEditor
          data={assets.data}
          onChange={handleDataChange}
          title="Additional Brand Data"
          excludeKeys={[]} // Can exclude specific keys if needed
        />
      </div>

      {/* Bottom Save Button (for long pages) */}
      {showSaveButton && hasChanges && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 py-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

